import {
  applyDocumentAddedState,
  applyDocumentRemovedState,
} from './documents-store-state'
import type { Dependencies } from '../../types/Dependencies'
import type { Doc } from '../../types/Doc'
import type {
  CreateDocumentRequest,
  DocumentsStore,
  DocumentsStoreApi,
} from './documents-store'

type DocumentsSetState = DocumentsStoreApi['setState']

const OPTIMISTIC_REV = '0-optimistic'
const OPTIMISTIC_ID_PREFIX = 'temp-document-'

export async function createDocumentAction({
  dependencies,
  request,
  set,
}: {
  dependencies: Dependencies
  request: CreateDocumentRequest
  set: DocumentsSetState
}) {
  const optimisticDocument = buildOptimisticDocument(request)

  set((state) =>
    applyDocumentAddedState({ document: optimisticDocument, state }),
  )

  try {
    const savedDocument = await dependencies.records.create(
      request.documentCollectionId,
      filterEmptyValues(request.values),
    )

    set((state) =>
      replaceOptimisticDocumentState({
        optimisticDocument,
        savedDocument,
        state,
      }),
    )

    return savedDocument
  } catch (error) {
    set((state) =>
      applyDocumentRemovedState({
        documentCollectionId: optimisticDocument.documentCollectionId,
        documentId: optimisticDocument._id,
        state,
      }),
    )

    throw error
  }
}

function buildOptimisticDocument(request: CreateDocumentRequest): Doc {
  return {
    _id: createOptimisticDocumentId(),
    _rev: OPTIMISTIC_REV,
    documentCollectionId: request.documentCollectionId,
    ...filterEmptyValues(request.values),
  }
}

function replaceOptimisticDocumentState({
  optimisticDocument,
  savedDocument,
  state,
}: {
  optimisticDocument: Doc
  savedDocument: Doc
  state: DocumentsStore
}) {
  const collectionId = optimisticDocument.documentCollectionId
  const newDocumentIds = state.newDocumentIdsByCollectionId[collectionId] ?? []

  return {
    items: state.items.map((item) =>
      item._id === optimisticDocument._id ? savedDocument : item,
    ),
    newDocumentIdsByCollectionId: {
      ...state.newDocumentIdsByCollectionId,
      [collectionId]: [
        savedDocument._id,
        ...newDocumentIds.filter(
          (id) => id !== optimisticDocument._id && id !== savedDocument._id,
        ),
      ],
    },
  }
}

function filterEmptyValues(values: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== ''),
  )
}

function createOptimisticDocumentId() {
  return `${OPTIMISTIC_ID_PREFIX}${createRandomId()}`
}

function createRandomId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}
